import React, { memo, useState } from 'react';
import AlidaLoader from '../common/AlidaLoader';
import { Card, Col, FormCheck, Row } from 'react-bootstrap';
import { CreatedApplicationsBarChart, CreatedApplicationsLineChart } from './Charts';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { DashboardStatistics } from '../../models/generics';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { AnimatedNumber } from './AnimatedNumber';
import { Tooltip as AntTooltip } from 'antd';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';

type ApplicationsWidgetProps = {
  appsByDate?: Record<string, number>;
}

export const CreatedApplicationsWidget = memo(({ appsByDate }: ApplicationsWidgetProps) => {
  const [showBarChart, setShowBarChart] = useState(false);

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.checked;
    setShowBarChart(value);
  }
  const data = appsByDate ? Object.entries(appsByDate).slice(-6).map(([date, value]) => ({ date, value })) : [];

  return (
    <Card>
      <Card.Body className="pt-2">
        <Row className="align-items-center mb-2">
          <Col>
            <h6 className="card-title mb-0">Created {applicationLabels.plural}</h6>
          </Col>
          <Col lg="auto">
            <FormCheck type="switch" id="applications-bar-chart" label="Bar Chart" checked={showBarChart} onChange={handleChange} />
          </Col>
        </Row>
        {!appsByDate && (
          <div className="py-5 text-center">
            <AlidaLoader size={80} />
          </div>
        )}
        {appsByDate && (
          <>
            {showBarChart ? <CreatedApplicationsBarChart data={data} /> : <CreatedApplicationsLineChart data={data} />}
          </>
        )}
      </Card.Body>
    </Card>
  )
});

type PropsWithStatistics = {
  statistics?: DashboardStatistics;
}

export const ApplicationsStatusWidget = memo(({ statistics }: PropsWithStatistics) => {

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  const COLORS = ['#10b981', '#f43f5e', '#eab308'];
  const data = [
    { name: "Completed", value: statistics?.bdaApplicationsCompleted },
    { name: "Failed", value: statistics?.bdaApplicationsFailed },
    { name: "Running", value: statistics?.bdaApplicationsRunning },
  ];

  return (
    <Card>
      <Card.Body className="pt-2">
        <h6 className="card-title">{applicationLabels.plural} Status</h6>
        {!statistics && (
          <div className="py-5 text-center">
            <AlidaLoader size={80} />
          </div>
        )}
        {statistics && (
          <ResponsiveContainer height={252}>
            <PieChart>
              <Pie
                data={data}
                outerRadius={90}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
                // label
                isAnimationActive
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card.Body>
    </Card>
  )
});

export const UserApplicationsWidget = memo(({ statistics }: PropsWithStatistics) => {

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));
  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));

  return (
    <Card>
      <Card.Body className="pt-2">
        <h6 className="card-title mb-2 d-inline-block me-2">My Stats</h6>
        <AntTooltip title={`${serviceLabels.plural || "Services"} or ${applicationLabels.plural} you have created`}>
          <FontAwesomeIcon icon={solid('info-circle')} />
        </AntTooltip>

        {!statistics && (
          <div className="py-5 text-center">
            <AlidaLoader size={80} />
          </div>
        )}
        {statistics && (
          <div className="p-1">
            <div className="d-flex justify-content-center align-items-center mb-2 text-alida-primary">
              <FontAwesomeIcon icon={solid('laptop-code')} />
              <h6 className="ms-2 mb-0">{applicationLabels.title}</h6>
            </div>
            <Row className="pt-1">
              <Col className="text-center">
                <AnimatedNumber value={statistics.myBdaApplicationsPrivate} size="md" />
                <FontAwesomeIcon icon={solid('user-secret')} fixedWidth size="sm" />
                <span className="ms-2">Private</span>
              </Col>
              <Col className="text-center">
                <AnimatedNumber value={statistics.myBdaApplicationsTeam} size="md" />
                <FontAwesomeIcon icon={solid('users')} fixedWidth size="sm" />
                <span className="ms-2">Team</span>
              </Col>
              <Col className="text-center">
                <AnimatedNumber value={statistics.myBdaApplicationsPublic} size="md" />
                <FontAwesomeIcon icon={solid('globe-europe')} fixedWidth size="sm" />
                <span className="ms-2">Public</span>
              </Col>
            </Row>

            <hr className="my-4" />

            <div className="d-flex justify-content-center align-items-center mb-2 text-service">
              <FontAwesomeIcon icon={solid('cubes')} />
              <h6 className="ms-2 mb-0">{serviceLabels.title}</h6>
            </div>
            <Row className="pt-1">
              <Col className="text-center">
                <AnimatedNumber value={statistics.myServicesPrivate} size="md" />
                <FontAwesomeIcon icon={solid('user-secret')} fixedWidth size="sm" />
                <span className="ms-2">Private</span>
              </Col>
              <Col className="text-center">
                <AnimatedNumber value={statistics.myServicesTeam} size="md" />
                <FontAwesomeIcon icon={solid('users')} fixedWidth size="sm" />
                <span className="ms-2">Team</span>
              </Col>
              <Col className="text-center">
                <AnimatedNumber value={statistics.myServicesPublic} size="md" />
                <FontAwesomeIcon icon={solid('globe-europe')} fixedWidth size="sm" />
                <span className="ms-2">Public</span>
              </Col>
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  )
})